import { rem } from 'polished'
import React, { useCallback } from 'react'
import styled from 'styled-components'
import TextButton from 'LuxKit/Button/TextButton'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import zIndex from 'styles/tools/z-index'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import { tileDesktopLayout } from './constants'
import Group from 'components/utils/Group'
import type { CounterChangeEventHandler } from 'LuxKit/Counter'
import Counter from 'LuxKit/Counter'
import BodyText from 'LuxKit/Typography/BodyText'
import MerchandiseMaxQuantityMessage from 'components/Merchandise/MerchandiseMaxQuantityMessage'
import { useIntl } from 'react-intl'

const MessageRow = styled.div`
  /* On mobile: visible, full width */
  width: 100%;
  display: flex;
  justify-content: flex-end;

  ${tileDesktopLayout} {
    /* On desktop: hidden, message shown in CounterAndMessageGroup instead */
    display: none;
  }
`

// Can't use <Group /> as our 'breakpoints' are container queries, not screen sizes
const ActionsContainer = styled.div`
  display: flex;
  justify-content: start;
  gap: ${rem(8)};
  flex-wrap: wrap;

  ${tileDesktopLayout} {
    flex-direction: column;
    justify-content: end;
    flex-wrap: nowrap;
  }
`

const ActionsRow = styled.div`
  z-index: ${zIndex.reviewCartButtons};
  display: flex;
  gap: ${rem(8)};
  width: 100%;
  justify-content: space-between;
  align-items: center;

  ${tileDesktopLayout} {
    width: auto;
    justify-content: end;
  }
`

const ButtonsGroup = styled.div`
  display: flex;
  gap: ${rem(8)};

  ${tileDesktopLayout} {
    justify-content: end;
  }
`

const CounterAndMessageGroup = styled.div`
  /* On mobile: hidden, counter is in ActionsRow instead */
  display: none;

  ${tileDesktopLayout} {
    /* On desktop: shown as flex column */
    display: flex;
    flex-direction: column;
    gap: ${rem(4)};
  }
`

const MobileCounterGroup = styled(Group)`
  /* On mobile: visible in ActionsRow */
  display: flex;

  ${tileDesktopLayout} {
    /* On desktop: hidden, shown in CounterAndMessageGroup instead */
    display: none;
  }
`

const DesktopCounterGroup = styled(Group)`
  /* On mobile: hidden */
  display: none;

  ${tileDesktopLayout} {
    /* On desktop: visible */
    display: flex;
  }
`

interface Props {
  variant: App.MerchandiseOfferVariant
  itemCount: number
  saveForLater?: boolean
  onSaveForLater?: React.MouseEventHandler<HTMLButtonElement>
  onDelete?: React.MouseEventHandler<HTMLButtonElement>
  onAddItem?: () => void
  onRemoveItem?: () => void
}

function MerchandiseCartReviewTileActions({
  variant,
  itemCount,
  saveForLater,
  onSaveForLater,
  onDelete,
  onAddItem,
  onRemoveItem,
}: Props) {
  const isBookable = !!variant.inventory

  const onQuantityCounterChange = useCallback<CounterChangeEventHandler>((value, _, e) => {
    e.preventDefault()
    if (value > itemCount) {
      onAddItem?.()
    } else {
      onRemoveItem?.()
    }
  }, [onAddItem, onRemoveItem, itemCount])

  const intl = useIntl()
  const isMaxQuantityReached = itemCount >= (variant.inventory ?? 0) && !!variant.inventory

  const saveForLaterLabel = intl.formatMessage({
    id: 'laKLQKQ68pmd27uW6v4R',
    defaultMessage: 'Save for later',
    description: 'Button text to save cart item for later in the shopping cart interface',
  })
  const addLabel = intl.formatMessage({
    id: '3pTncErYaDMSpmM4XxiN',
    defaultMessage: 'Add',
    description: 'Button text to add saved item back to cart in the cart view',
  })
  const deleteLabel = intl.formatMessage({
    id: 'JvRaptmTpdkakbfBSvjc',
    defaultMessage: 'Delete',
    description: 'Button text to delete item from cart in the shopping cart interface',
  })
  const qtyLabel = intl.formatMessage({
    id: 'Htqf394tDhoKj9uNqgHp',
    defaultMessage: 'Qty',
    description: 'Quantity label for merchandise item counter in the shop section',
  })

  const counterContent = <>
    <BodyText variant="small" weight="bold">{qtyLabel}</BodyText>
    <Counter
      value={itemCount}
      onChange={onQuantityCounterChange}
      min={1}
      max={variant.inventory}
      size="small"
    />
  </>

  const messageContent = isMaxQuantityReached ? <MerchandiseMaxQuantityMessage /> : null

  return <ActionsContainer>
    <ActionsRow>
      <ButtonsGroup>
        {isBookable && <>
          {!saveForLater && <TextButton
            kind="secondary"
            size="small"
            shape="round"
            onClick={onSaveForLater}
          >
            {saveForLaterLabel}
          </TextButton>}
          {saveForLater && <TextButton
            kind="primary"
            size="small"
            shape="round"
            startIcon={<LinePlusIcon />}
            onClick={onSaveForLater}
          >
            {addLabel}
          </TextButton>}
        </>}
        <TextButton
          kind="secondary"
          size="small"
          shape="round"
          startIcon={<LineTrashAltIcon />}
          onClick={onDelete}
        >
          {deleteLabel}
        </TextButton>
      </ButtonsGroup>
      {!!isBookable && <MobileCounterGroup
        direction="horizontal"
        gap={8}
        verticalAlign="center"
        horizontalAlign="end"
      >
        {counterContent}
      </MobileCounterGroup>}
    </ActionsRow>
    {!!isBookable && <CounterAndMessageGroup>
      <DesktopCounterGroup
        direction="horizontal"
        gap={8}
        verticalAlign="center"
        horizontalAlign="end"
      >
        {counterContent}
      </DesktopCounterGroup>
      {messageContent}
    </CounterAndMessageGroup>}
    <MessageRow>
      {messageContent}
    </MessageRow>
  </ActionsContainer>
}

export default MerchandiseCartReviewTileActions
